// src/pages/customer-panel/support/TicketDetailPage.js
// Support Ticket Detail Page

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    TextField,
    Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    OPEN:             { color: '#0ea5e9', bg: '#e0f2fe', label: 'Open' },
    IN_PROGRESS:      { color: '#f59e0b', bg: '#fef3c7', label: 'In Progress' },
    RESOLVED:         { color: '#10b981', bg: '#d1fae5', label: 'Resolved' },
    CLOSED:           { color: '#6b7280', bg: '#f3f4f6', label: 'Closed' },
    ESCALATED:        { color: '#ef4444', bg: '#fee2e2', label: 'Escalated' },
    PENDING_CUSTOMER: { color: '#8b5cf6', bg: '#ede9fe', label: 'Pending Customer' },
};

const PRIORITY_CONFIG = {
    LOW:      { color: '#6b7280', bg: '#f3f4f6', label: 'Low' },
    MEDIUM:   { color: '#f59e0b', bg: '#fef3c7', label: 'Medium' },
    HIGH:     { color: '#ef4444', bg: '#fee2e2', label: 'High' },
    CRITICAL: { color: '#dc2626', bg: '#fecaca', label: 'Critical' },
};

export default function TicketDetailPage() {
    const { ticketId } = useParams();
    const navigate = useNavigate();

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [commentContent, setCommentContent] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchTicket = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/api/customer/tickets/${ticketId}`);
            setTicket(response.data.data || response.data.ticket || response.data);
        } catch (err) {
            console.error('Failed to fetch ticket:', err);
            setError(err.response?.data?.message || 'Failed to load ticket details.');
        } finally {
            setLoading(false);
        }
    }, [ticketId]);

    useEffect(() => {
        document.title = 'Ticket Details | Customer Panel';
        fetchTicket();
    }, [fetchTicket]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return '--';
        }
    };

    const getStatusChip = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.OPEN;
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: config.bg,
                    color: config.color,
                }}
            />
        );
    };

    const getPriorityChip = (priority) => {
        const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.LOW;
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: config.bg,
                    color: config.color,
                }}
            />
        );
    };

    const handleAddComment = async () => {
        if (!commentContent.trim()) return;
        setSubmittingComment(true);
        try {
            await axiosInstance.post(`/api/customer/tickets/${ticketId}/comments`, {
                content: commentContent.trim(),
            });
            setCommentContent('');
            setSnackbar({ open: true, message: 'Comment added successfully.', severity: 'success' });
            fetchTicket();
        } catch (err) {
            console.error('Failed to add comment:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to add comment.',
                severity: 'error',
            });
        } finally {
            setSubmittingComment(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress size={40} sx={{ color: GREEN_ACCENT }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/support')}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b', mb: 2 }}
                >
                    Back to Tickets
                </Button>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!ticket) return null;

    const comments = ticket.comments || [];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
            {/* Back Button + Refresh */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/support')}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                >
                    Back to Tickets
                </Button>
                <IconButton onClick={fetchTicket} sx={{ color: '#64748b' }}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {/* Ticket Header */}
            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    mb: 3,
                }}
            >
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    {/* Ticket Number + Status + Priority */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: GREEN_ACCENT, fontSize: '0.9rem' }}>
                            {ticket.ticket_number || '--'}
                        </Typography>
                        {getStatusChip(ticket.status)}
                        {getPriorityChip(ticket.priority)}
                    </Box>

                    {/* Subject */}
                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily,
                            fontWeight: 700,
                            color: '#1e293b',
                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                            mb: 1.5,
                        }}
                    >
                        {ticket.subject || '--'}
                    </Typography>

                    {/* Created date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                            Created {formatDate(ticket.created_at)}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Description */}
                    <Typography
                        sx={{
                            fontFamily,
                            color: '#475569',
                            fontSize: '0.95rem',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {ticket.description || 'No description provided.'}
                    </Typography>
                </Box>

                {/* Linked Appointment */}
                {ticket.appointment_id && (
                    <>
                        <Divider />
                        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 1 }}>
                                Linked Appointment
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {ticket.appointment?.title && (
                                    <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.9rem' }}>
                                        <strong>Title:</strong> {ticket.appointment.title}
                                    </Typography>
                                )}
                                {ticket.appointment?.date && (
                                    <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.9rem' }}>
                                        <strong>Date:</strong> {formatDate(ticket.appointment.date)}
                                    </Typography>
                                )}
                                {ticket.appointment?.status && (
                                    <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.9rem' }}>
                                        <strong>Status:</strong> {ticket.appointment.status}
                                    </Typography>
                                )}
                                {!ticket.appointment && (
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                        Appointment ID: {ticket.appointment_id}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </Paper>

            {/* Comments Section */}
            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                        Comments ({comments.length})
                    </Typography>
                </Box>

                {/* Comments List */}
                {comments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <SupportAgentIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>
                            No comments yet. Be the first to add one.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                        {comments.map((comment, index) => (
                            <Box
                                key={comment.id || index}
                                sx={{
                                    p: { xs: 2, md: 3 },
                                    borderBottom: index < comments.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'grey.100',
                                    '&:hover': { bgcolor: '#fafbfc' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PersonIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                        {comment.author_name || comment.user_name || 'Unknown'}
                                    </Typography>
                                    {comment.is_staff && (
                                        <Chip
                                            label="Staff"
                                            size="small"
                                            sx={{
                                                fontFamily,
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                bgcolor: '#e0f2fe',
                                                color: '#0ea5e9',
                                            }}
                                        />
                                    )}
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.8rem', ml: 'auto' }}>
                                        {formatDate(comment.created_at)}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily,
                                        color: '#475569',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.6,
                                        whiteSpace: 'pre-wrap',
                                        pl: 3.5,
                                    }}
                                >
                                    {comment.content || comment.body || ''}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Add Comment Form */}
                <Box
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderTop: '1px solid',
                        borderColor: 'grey.100',
                        bgcolor: '#f8fafc',
                    }}
                >
                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem', mb: 1.5 }}>
                        Add a Comment
                    </Typography>
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="Type your comment here..."
                        value={commentContent}
                        onChange={(e) => setCommentContent(e.target.value)}
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-root': {
                                fontFamily,
                                borderRadius: '10px',
                                bgcolor: '#fff',
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            endIcon={submittingComment ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />}
                            onClick={handleAddComment}
                            disabled={submittingComment || !commentContent.trim()}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '10px',
                                bgcolor: GREEN_ACCENT,
                                '&:hover': { bgcolor: '#059669' },
                                px: 3,
                            }}
                        >
                            {submittingComment ? 'Sending...' : 'Send Comment'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
